<template>
  <div v-if="loading">
    <v-skeleton-loader type="card@4" class="ma-1" />
  </div>
  <div v-else key="loaded" class="ma-1 bg-surface">
    <approved-domain-card />
    <approved-domain-actions />
    <view-tabs v-model="activeTab" :tabs="tabs" />
  </div>
</template>

<script setup>
import ViewTabs from "@/components/shared/ViewTabs.vue";
import ApprovedDomainCard from "@/components/approved-domain/ApprovedDomainCard.vue";
import ApprovedDomainActions from "@/components/approved-domain/ApprovedDomainActions.vue";
import ApprovedDomainSettings from "@/components/approved-domain/ApprovedDomainSettings.vue";
import ApprovedDomainTheme from "@/components/approved-domain/ApprovedDomainTheme.vue";
import ApprovedDomainCarriers from "@/components/approved-domain/ApprovedDomainCarriers.vue";
import ApprovedDomainAccess from "@/components/approved-domain/ApprovedDomainAccess.vue";
import ApprovedDomainMarketing from "@/components/approved-domain/ApprovedDomainMarketing.vue";
import ApprovedDomainCommissions from "@/components/approved-domain/ApprovedDomainCommissions.vue";
import ApprovedDomainAnalytics from "@/components/approved-domain/ApprovedDomainAnalytics.vue";
import ApprovedDomainProducts from "@/components/approved-domain/ApprovedDomainProducts.vue";
import ApprovedDomainRefer from "@/components/approved-domain/ApprovedDomainRefer.vue";
import ApprovedDomainIntegrations from "@/components/approved-domain/ApprovedDomainIntegrations.vue";

import { parseErrorMessage } from "@/util/helpers";
import { storeToRefs } from "pinia";

import { useApprovedDomainView } from "@/stores/approved-domain-view";
import { useSnackbarStore } from "@/stores/snackbar";
import { useInstanceStore } from "@/stores/instance";
import { useHead } from "@unhead/vue";
import { markRaw, ref, onBeforeUnmount, computed, watch, toRefs } from "vue";
import { useRouter } from "vue-router";
import {
  mdiCogs,
  mdiCube,
  mdiDomain,
  mdiFileDocumentEdit,
  mdiListBox,
  mdiPalette,
  mdiPoll,
  mdiSetSplit,
  mdiShieldAccount,
  mdiShopping
} from "@mdi/js";
import { useTheme } from "vuetify";

const props = defineProps({
  id: {
    type: [Number, String],
    required: true
  },
  page: {
    type: String,
    required: false,
    default: ""
  },
  scrollTo: {
    type: String,
    required: false,
    default: ""
  }
});

const instance = useInstanceStore();
const approvedDomain = useApprovedDomainView();
const snackbar = useSnackbarStore();

const router = useRouter();
const theme = useTheme();

const { breadcrumb } = storeToRefs(instance);
const { domain, isSignUpOrSelectAgent, isReferral, lockChanges } =
  storeToRefs(approvedDomain);

const head = useHead({ title: "Website" });

const loading = ref(false);
const activeTab = ref(0);
const { page: pageProp, scrollTo: scrollToProp } = toRefs(props);

onBeforeUnmount(() => (breadcrumb.value = ""));

const tabs = computed(() => {
  return [
    {
      visible: !lockChanges.value,
      icon: mdiFileDocumentEdit,
      title: "Details",
      component: markRaw(ApprovedDomainSettings),
      page: "details"
    },
    {
      visible: isReferral.value,
      icon: mdiListBox,
      title: "Refer Settings",
      component: markRaw(ApprovedDomainRefer),
      page: "refer-settings"
    },
    {
      visible: !isSignUpOrSelectAgent.value && !lockChanges.value,
      icon: mdiPalette,
      title: "Theme & Colors",
      component: markRaw(ApprovedDomainTheme),
      page: "theme"
    },
    {
      visible: !isSignUpOrSelectAgent.value && !lockChanges.value,
      icon: mdiCogs,
      title: "Integrations",
      component: markRaw(ApprovedDomainIntegrations),
      page: "integrations"
    },
    {
      visible: !isSignUpOrSelectAgent.value && !lockChanges.value,
      icon: mdiDomain,
      title: "Carriers",
      component: markRaw(ApprovedDomainCarriers),
      page: "carriers"
    },
    {
      visible: !isSignUpOrSelectAgent.value && !lockChanges.value,
      icon: mdiCube,
      title: "Products",
      component: markRaw(ApprovedDomainProducts),
      page: "products"
    },
    {
      visible: !lockChanges.value,
      icon: mdiShieldAccount,
      title: "Access",
      component: markRaw(ApprovedDomainAccess),
      page: "access"
    },
    {
      visible: !isSignUpOrSelectAgent.value && !lockChanges.value,
      icon: mdiSetSplit,
      title: "Commissions",
      component: markRaw(ApprovedDomainCommissions),
      page: "commissions"
    },
    {
      visible: true,
      icon: mdiShopping,
      title: "Marketing",
      component: markRaw(ApprovedDomainMarketing),
      page: "marketing"
    },
    {
      visible: !lockChanges.value,
      icon: mdiPoll,
      title: "Analytics",
      component: markRaw(ApprovedDomainAnalytics),
      page: "analytics"
    }
  ].filter(f => f.visible);
});

watch(tabs, () => {
  if (activeTab.value > tabs.value.length) activeTab.value = 0;
});

watch(activeTab, (newV, oldV) => {
  if (newV === oldV) return;
  handleQueryChange();
});

function handleQueryChange(initializing = false) {
  let page;
  if (initializing) {
    const tempPage = tabs.value.findIndex(v => v.page === pageProp.value);

    if (tempPage > -1) {
      page = tabs.value[tempPage].page;
      activeTab.value = tempPage;
    }
  } else {
    page = tabs.value[activeTab.value].page;
  }

  if (pageProp.value === page || !page) return;
  router.replace({ query: { page: page } });
}

function handleFocus() {
  if (!scrollToProp.value) return;
  const el = document.getElementById(scrollToProp.value);
  if (!el) return;
  el.parentNode.scrollIntoView({ behavior: "smooth", block: "center" });
  el.parentNode.classList.add("focusable-div");
  const focusClass = theme.current.value.dark
    ? "focused-div-dark"
    : "focused-div-light";

  el.parentNode.classList.add(focusClass);
  setTimeout(() => {
    el.parentNode.classList.remove(focusClass);
  }, 4000);
}

async function getApprovedDomainInfo() {
  loading.value = true;
  try {
    await approvedDomain.initializeApprovedDomain(props.id);
    breadcrumb.value = domain.value;
    head.patch({ title: domain.value });
  } catch (e) {
    snackbar.showErrorSnackbar({
      message: parseErrorMessage(e),
      timeout: 5000
    });
    await router.replace({ name: "Home" });
  } finally {
    loading.value = false;
  }
}

getApprovedDomainInfo().then(async () => {
  handleQueryChange(true);
  await new Promise(r => setTimeout(r, 500));
  handleFocus();
});
</script>
